// component for whole form

import "../assets/scss/main.scss";

import { upperFirst } from "lodash-es";
import { MouseEvent, useState } from "react";
import { useNavigate } from "react-router-dom";

import { CreateAndEditFieldType, Field, Group } from "../types";
import { Form, FormDataType } from "../types";
import { notify } from "../utils/notify";
import { renderFormField } from "../utils/renderFormField";
import { validateFormFields } from "../utils/validateFormFields";

type FormFieldsProps = {
  tournamentId?: string;
  ticketId?: string;
  setTicketGroups?: React.Dispatch<React.SetStateAction<Group[]>>;
  fields: CreateAndEditFieldType[] | Field[];
  form: Form;
  setFormData: (id: string, data: FormDataType) => void;
  formMode: string;
  isCustomForm?: boolean;
  isModal?: boolean;
  name?: string;
  onGetFunction?: (id: number) => Promise<any>;
  onCreateFunction?: (data: object) => Promise<any>;
  onUpdateFunction?: (data: object) => Promise<any>;
  onPostFunction?: (data: object) => Promise<any>;
  afterCreateFunction?: (data: object) => Promise<any>;
  afterUpdateFunction?: (data: object) => Promise<any>;
  afterPostFunction?: (data: object) => Promise<any>;
  setFormModal?: React.Dispatch<React.SetStateAction<boolean>>;
  fieldOptionData?: Record<string, any[]>;
  showBackButton?: boolean;
  recordId?: number;
};

export const FormFields = ({
  tournamentId,
  ticketId,
  fields,
  form,
  setFormData,
  formMode,
  isCustomForm,
  isModal = false,
  name,
  onGetFunction,
  onCreateFunction,
  onUpdateFunction,
  onPostFunction,
  afterCreateFunction,
  afterUpdateFunction,
  afterPostFunction,
  setFormModal,
  fieldOptionData,
  showBackButton = true,
  recordId,
}: FormFieldsProps) => {
  const navigate = useNavigate();
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const [selectedSuggestionOtps, setSelectedSuggestionOtps] = useState<
    Record<string, string>
  >({});

  const setFormKeyValue = ({
    key,
    value,
  }: {
    key: string;
    value: string | boolean;
  }) => {
    // Handle suggestion input type and show value in input field instead of ID's
    const suggestionField = fields.find(
      (ele) => ele.inputType === "suggestion" && key === ele.fieldName
    );
    if (suggestionField && value && typeof value === "string") {
      try {
        const data = JSON.parse(value);
        setSelectedSuggestionOtps((prev) => ({
          ...prev,
          [key]: data.id,
        }));
        setFormData(form.method, {
          ...form.data,
          [key]: data.value,
        });
        return;
      } catch {
        // Value is not a JSON-wrapped suggestion (e.g. free-text entry via returnNonMatchingInput).
        // Fall through to set the raw string value directly.
        setFormData(form.method, {
          ...form.data,
          [key]: value,
        });
        return;
      }
    } else {
      setFormData(form.method, {
        ...form.data,
        [key]: value,
      });
    }
  };

  const validateForm = () => {
    let isValid = true;

    try {
      validateFormFields(fields, form.data);
    } catch (err) {
      notify.error(
        typeof err === "string"
          ? err
          : err instanceof Error
            ? err.message
            : "An error occurred"
      );
      isValid = false;
    }

    return isValid;
  };

  const onSubmit = async (evt: MouseEvent<HTMLButtonElement>) => {
    try {
      evt.preventDefault();
      if (validateForm()) {
        const finalFormData = { ...form.data };
        // If any field is a suggestion, update its value to the selected suggestion ID for that field
        fields.forEach((field) => {
          if (
            field.inputType === "suggestion" &&
            selectedSuggestionOtps[field.fieldName]
          ) {
            finalFormData[field.fieldName] =
              selectedSuggestionOtps[field.fieldName];
          }
        });
        if (form.method) {
          setSubmitDisabled(true);
          if (form.method === "post") {
            if (onPostFunction) {
              await onPostFunction(finalFormData);
              if (afterPostFunction) {
                await afterPostFunction(finalFormData);
              }
            } else {
              throw new Error("onPostFunction Required for form.method post");
            }
          }

          if (form.method === "create") {
            if (onCreateFunction) {
              // append round toggle fields
              if (name === "round") {
                finalFormData.enabledDisabledTicketCategories = [];
                Object.keys(finalFormData).forEach((k: string) => {
                  if (k.includes("ticketCategoryId")) {
                    (
                      finalFormData.enabledDisabledTicketCategories as FormDataType[]
                    ).push({
                      ticketCategoryId: k.match(/\d+/g)![0],
                      enabled: finalFormData[k] ? true : false,
                    });
                  }
                });
                setFormData("", finalFormData);
              }
              await onCreateFunction(finalFormData);

              if (afterCreateFunction) {
                await afterCreateFunction(finalFormData);
              } else {
                // If no afterCreateFunction is provided, a default success message is shown and the page reloads.
                // Provide an afterCreateFunction for custom messaging, navigation, or other logic.
                notify.persistToast({
                  message: `New ${name} successfully created.`,
                  type: "success",
                });
                window.location.reload();
              }

              if (setFormModal) {
                if (name === "round" && tournamentId && onGetFunction) {
                  await onGetFunction(parseInt(tournamentId));
                  setFormModal(false);
                }
              }
            } else {
              throw new Error(
                "onCreateFunction Required for form.method create"
              );
            }
          }

          if (form.method === "edit") {
            if (onUpdateFunction) {
              if (name === "round") {
                finalFormData.enabledDisabledTicketCategories = [];
                Object.keys(finalFormData).forEach((k: string) => {
                  if (k.includes("ticketCategoryId")) {
                    (
                      finalFormData.enabledDisabledTicketCategories as FormDataType[]
                    ).push({
                      ticketCategoryId: k.match(/\d+/g)![0],
                      enabled: finalFormData[k] ? true : false,
                    });
                  }
                });
                setFormData("", finalFormData);
              }

              await onUpdateFunction(finalFormData);
              notify.success(`${upperFirst(name)} successfully updated.`);

              if (setFormModal) {
                if (name === "round" && tournamentId && onGetFunction) {
                  await onGetFunction(parseInt(tournamentId));
                  setFormModal(false);
                }
                if (name === "Group" && ticketId && onGetFunction) {
                  await onGetFunction(parseInt(ticketId));
                  setFormModal(false);
                }
              }
              if (afterUpdateFunction) {
                await afterUpdateFunction(finalFormData);
              }
            } else {
              throw new Error("onUpdateFunction Required for form.method edit");
            }
          }
        }
      }
    } finally {
      setSubmitDisabled(false);
    }
  };

  let submitText;
  if (formMode === "post") {
    submitText = "Submit";
  } else if (formMode === "edit") {
    submitText = "Save Changes";
  } else if (formMode === "create") {
    submitText = "Create";
  }

  return (
    <div className="formWrapper">
      <form>
        <div className="formFields">
          {fields.map((field) =>
            renderFormField(
              field,
              form.data,
              setFormKeyValue,
              formMode,
              fieldOptionData?.[field.fieldName],
              recordId
            )
          )}
        </div>

        {!isCustomForm && (
          <section className="btnGroup">
            {formMode !== "read" && (
              <button
                className="submitFormBtn"
                onClick={onSubmit}
                disabled={submitDisabled}
              >
                {submitText}
              </button>
            )}
            {!isModal && showBackButton && (
              <button
                className="backFormBtn"
                onClick={(e) => {
                  e.preventDefault();
                  navigate(-1);
                }}
              >
                Back
              </button>
            )}
            {isModal && setFormModal && (
              <button
                className="cancelFormBtn"
                onClick={() => setFormModal(false)}
              >
                Cancel
              </button>
            )}
          </section>
        )}
      </form>
    </div>
  );
};

export default FormFields;
